import React, { FC, useEffect, useState } from "react";

import { DataViewKey, useDimensions } from "@keepeek/commons";
import { BaseComponentProps, useThemeColors } from "@keepeek/refront-components";
import { getAccessibleTextColor } from "@keepeek/refront-components";
import { Box, useTheme } from "@mui/material";
import clsx from "clsx";

import { KHeaderWidget as KHeaderWidgetModel } from "../../../models/configuration/components/kHeaderWidget";
import BasketToggler from "../../Basket/BasketToggler";
import { KWidgetProps } from "../models";
import { useKHeaderWidget } from "./hooks/useKHeaderWidget";
import KHeaderWidgetActionsBox from "./KHeaderWidgetActionsBox";
import KHeaderWidgetBurger from "./KHeaderWidgetBurger";
import KHeaderWidgetLogoBox from "./KHeaderWidgetLogoBox";
import { KHeaderWidgetComponent, KHeaderWidgetComponentContext } from "./models";
import StyledKHeaderWidget, { classes } from "./styles";

export type KHeaderWidgetProps = {
  widgetId: KHeaderWidgetModel["id"];
  dataViewKey: DataViewKey;
} & Omit<KHeaderWidgetModel, "id"> &
  BaseComponentProps &
  KWidgetProps;

const KHeaderWidget: FC<React.PropsWithChildren<KHeaderWidgetProps>> = function ({
  testId = "KHeaderWidget",
  id,
  className,
  widgetId,
  dataViewKey,
}) {
  const theme = useTheme();
  const { convertColor } = useThemeColors();
  const { ref, dimensions } = useDimensions({});
  const [barWidth, setBarWith] = useState(dimensions.width);
  useEffect(() => {
    setBarWith(dimensions.width);
  }, [ref, dimensions.width]);
  const { configuration, components } = useKHeaderWidget({ widgetId, barWidth });
  return (
    <StyledKHeaderWidget
      component="header"
      role="banner"
      data-testid={testId}
      id={id}
      className={clsx("KHeaderWidget", className, classes.root)}
      backgroundColor={convertColor(configuration?.backgroundColor)}
      logoPosition={configuration?.kHeaderWidgetLogo?.kHeaderWidgetLogoPosition}
      ref={ref}
      barWidth={barWidth}
    >
      <Box
        id={id}
        data-testid="KHeaderWidgetNav"
        component="nav"
        role="navigation"
        className={clsx("KHeaderWidgetNav", classes.nav)}
      >
        {/* Burger menu */}
        <KHeaderWidgetBurger
          widgetId={widgetId}
          className={classes.burger}
          dataViewKey={dataViewKey}
          textColor={getAccessibleTextColor(
            theme,
            convertColor(configuration?.backgroundColor) || theme.palette.common.white,
          )}
        />
        {/* logo */}
        <KHeaderWidgetLogoBox widgetId={widgetId} className={classes.logoBox} />
        {/* Actions in top bar context */}
        <KHeaderWidgetActionsBox
          widgetId={widgetId}
          context={KHeaderWidgetComponentContext.TopBar}
          className={classes.actionsBox}
          dataViewKey={dataViewKey}
        />
        {/* Basket */}
        {components.currentComponents.includes(KHeaderWidgetComponent.Basket) && (
          <Box className={classes.basketBox}>
            <Box>
              <BasketToggler
                showCount={
                  configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsBasket?.bulletActive
                }
                backgroundColor={
                  configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsBasket?.backgroundColor
                }
                color={configuration?.kHeaderWidgetActions?.kHeaderWidgetActionsBasket?.color}
              />
            </Box>
          </Box>
        )}
      </Box>
    </StyledKHeaderWidget>
  );
};

export default KHeaderWidget;
